import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import api from '../services/api'
import logoEspaceClientTexte from '../images/Logo-Espace-client-texte.png'
import logoEspaceClientTexteDark from '../images/Logo-Espace-client-texte-dark.png'
import PasswordInput from '../components/PasswordInput'
import { useSeo } from '../hooks/useSeo'

interface SupplierBrand {
  erp_name: string
  logo: string | null
}

export default function Login() {
  const { t } = useTranslation()
  useSeo({ title: t('seo.loginTitle') })
  const { siren } = useParams<{ siren?: string }>()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [supplier, setSupplier] = useState<SupplierBrand | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!siren) return
    api
      .getERPBrandBySiren(siren)
      .then((data) => setSupplier({ erp_name: data.erp_name, logo: data.logo }))
      .catch(() => {
        /* SIREN not found — fall back to normal login silently */
      })
  }, [siren])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const data = await api.login(email, password)
      const role = data.user?.role
      // Admin and super_admin users go directly to the admin panel
      if (role === 'admin' || role === 'super_admin') {
        navigate('/admin')
      } else {
        navigate('/dashboard')
      }
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setError(apiErr.response?.data?.error || 'Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMS4xLS45LTItMi0yaC00Yy0xLjEgMC0yIC45LTIgMnY0YzAgMS4xLjkgMiAyIDJoNGMxLjEgMCAyLS45IDItMnYtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-primary-500 via-accent-500 to-primary-600"></div>

        <div className="p-8 sm:p-12">
          <div className="text-center mb-8">
            {supplier ? (
              <>
                {supplier.logo ? (
                  <img
                    src={supplier.logo}
                    alt={supplier.erp_name}
                    className="h-20 mx-auto mb-3 object-contain"
                  />
                ) : (
                  <div className="h-20 flex items-center justify-center mb-3">
                    <span className="text-2xl font-bold text-primary-700">{supplier.erp_name}</span>
                  </div>
                )}
                <p className="text-gray-500 text-sm mt-1">{t('login.supplierSubtitle')}</p>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <Link
                    to="/"
                    aria-label={t('common.backToHome')}
                    title={t('common.backToHome')}
                    className="group inline-flex items-center rounded-lg opacity-50 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                  >
                    <img
                      src={logoEspaceClientTexte}
                      alt="Espace Client"
                      className="h-6 w-auto transition-transform group-hover:scale-105 dark:hidden"
                    />
                    <img
                      src={logoEspaceClientTexteDark}
                      alt=""
                      aria-hidden="true"
                      className="hidden h-6 w-auto transition-transform group-hover:scale-105 dark:block"
                    />
                  </Link>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  aria-label={t('common.backToHome')}
                  title={t('common.backToHome')}
                  className="group inline-flex items-center rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 mb-4"
                >
                  <img
                    src={logoEspaceClientTexte}
                    alt="Espace Client"
                    className="h-14 w-auto transition-transform group-hover:scale-105 dark:hidden"
                  />
                  <img
                    src={logoEspaceClientTexteDark}
                    alt=""
                    aria-hidden="true"
                    className="hidden h-14 w-auto transition-transform group-hover:scale-105 dark:block"
                  />
                </Link>
                <p className="text-gray-600">{t('login.subtitle')}</p>
              </>
            )}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-slide-down">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                placeholder={t('login.emailPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('login.password')}
              </label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('login.loading') : t('login.submit')}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <Link
              to="/forgot-password"
              className="block text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('login.forgotPassword')}
            </Link>
            <Link
              to="/register"
              className="block text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('login.registerRequest')}
            </Link>
          </div>

          {/* Sélecteur de langue */}
          <div className="mt-4 flex justify-center">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => i18n.changeLanguage('fr')}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                  i18n.language === 'fr' || i18n.language?.startsWith('fr')
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                FR
              </button>
              <button
                onClick={() => i18n.changeLanguage('en')}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                  i18n.language === 'en' || i18n.language?.startsWith('en')
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
